import { Extension } from "@tiptap/core";
import { PluginKey } from "@tiptap/pm/state";
import { createElement } from "react";
import { Suggestion } from "@tiptap/suggestion";
import type { Variables } from "@/lib/sparxs/metadataSchemas";
import {
  mountSuggestionRoot,
  unmountSuggestionRoot,
  type SuggestionReactRoot,
} from "../lib/suggestionReactRoot";
import { VariableSuggestionList } from "./VariableSuggestionList";
import type { VariableSuggestionItem } from "./VariableSuggestionList";

export interface VariableSuggestionExtensionOptions {
  char: string;
  variables: Variables[];
}

const variableToItem = (v: Variables): VariableSuggestionItem => ({
  raw: v.name,
  label: v.name,
});

const filterItems = (
  variables: Variables[],
  query: string,
): VariableSuggestionItem[] => {
  const list = variables.map(variableToItem);
  if (!query.trim()) return list;
  const q = query.toLowerCase();
  return list.filter((item) => item.label.toLowerCase().includes(q));
};

export const VariableSuggestionExtension =
  Extension.create<VariableSuggestionExtensionOptions>({
    name: "variableSuggestion",

    addOptions() {
      return {
        char: "{",
        variables: [],
      };
    },

    addProseMirrorPlugins() {
      const { variables } = this.options;
      const { editor } = this;
      const pluginKey = new PluginKey("variableSuggestion");
      const stateRef: {
        container: HTMLDivElement | null;
        root: SuggestionReactRoot | null;
        selectedIndex: number;
        currentProps: {
          items: VariableSuggestionItem[];
          command: (item: VariableSuggestionItem) => void;
          clientRect: (() => DOMRect | null) | null;
        } | null;
        update: () => void;
      } = {
        container: null,
        root: null,
        selectedIndex: 0,
        currentProps: null,
        update: () => undefined,
      };

      return [
        Suggestion<VariableSuggestionItem, VariableSuggestionItem>({
          pluginKey,
          editor: this.editor,
          char: this.options.char,
          allowedPrefixes: null,
          items: ({ query }) => {
            return filterItems(variables, query);
          },
          command: ({ editor, range, props }) => {
            editor
              .chain()
              .focus()
              .deleteRange(range)
              .insertContent({
                type: "variableToken",
                attrs: { raw: props.raw },
              })
              .run();
          },
          render: () => ({
            onStart: (props) => {
              stateRef.currentProps = {
                items: props.items,
                command: props.command,
                clientRect: props.clientRect ?? null,
              };
              stateRef.selectedIndex = 0;
              const mounted = mountSuggestionRoot(
                editor.view.dom.ownerDocument,
              );
              stateRef.container = mounted.container;
              stateRef.root = mounted.root;
              stateRef.update = () => {
                if (stateRef.root == null || stateRef.currentProps == null)
                  return;
                stateRef.root.render(
                  createElement(VariableSuggestionList, {
                    items: stateRef.currentProps.items,
                    selectedIndex: stateRef.selectedIndex,
                    onSelect: (item) => {
                      stateRef.currentProps?.command(item);
                      stateRef.currentProps = null;
                      stateRef.update = () => undefined;
                    },
                    clientRect: stateRef.currentProps.clientRect,
                  }),
                );
              };
              stateRef.update();
            },
            onUpdate: (props) => {
              stateRef.currentProps = {
                items: props.items,
                command: props.command,
                clientRect: props.clientRect ?? null,
              };
              stateRef.selectedIndex = Math.min(
                stateRef.selectedIndex,
                Math.max(0, props.items.length - 1),
              );
              stateRef.update();
            },
            onExit: () => {
              unmountSuggestionRoot(stateRef.container, stateRef.root);
              stateRef.container = null;
              stateRef.root = null;
              stateRef.currentProps = null;
              stateRef.update = () => undefined;
            },
            onKeyDown: ({ event }) => {
              const props = stateRef.currentProps;
              if (props == null || props.items.length === 0) return false;
              if (event.key === "ArrowDown") {
                stateRef.selectedIndex = Math.min(
                  stateRef.selectedIndex + 1,
                  props.items.length - 1,
                );
                stateRef.update();
                return true;
              }
              if (event.key === "ArrowUp") {
                stateRef.selectedIndex = Math.max(
                  stateRef.selectedIndex - 1,
                  0,
                );
                stateRef.update();
                return true;
              }
              if (event.key === "Enter") {
                const item = props.items[stateRef.selectedIndex];
                if (item != null) {
                  props.command(item);
                }
                return true;
              }
              return false;
            },
          }),
        }),
      ];
    },
  });
